import React, { useEffect } from "react"
import { graphql } from "gatsby"
import { useTranslation } from "gatsby-plugin-react-i18next"
import { getValidLocaleCode } from "@utils/localisation"
import { formatDate, formatCurrencyFinancialToParts } from "@utils/formatNumber"
import { slugMaterialName } from "../utils/helpers"
import CasesCarrouselSection from "../modules/HomePage/CasesCarrouselSection"

import ScrapMetalPriceIndex from "@modules/PriceIndexPage/PriceSection"
import ListOfCountries from "@modules/PriceIndexPage/ListOfCountries"

import MessageBanner from "@components/MessageBanner"
import BreadCrumb from "@components/BreadCrumb"
import TextBanner from "@components/TextBanner"

import Layout from "@components/Layout"
import HeaderMetal from "@components/HeaderMetal"
import ScrapMetalPriceDetail from "@modules/ScrapMetalPriceDetail"
import BuySellBanner from "@components/BuySellBanner"

import OperationalExcellence from "@modules/Metals/operationalExcellence"

import { PriceAlertsBanner } from "@modules/PriceAlertsBanner"

import aluminiumHero from "@images/aluminiumHero.jpg"
import bronzeHero from "@images/bronzeHero.jpg"
import copperHero from "@images/cooperHero.jpg"
import brassHero from "@images/brassHero.jpg"
import stainlessSteelHero from "@images/stainless-steelHero.jpg"
import ironAndSteelHero from "@images/iron-and-steelHero.jpg"
import leadHero from "@images/leadHero.jpg"
import zincHero from "@images/zincHero.jpg"
import tinHero from "@images/tinHero.jpg"
import nickelHero from "@images/nickelHero.jpg"

const heroBackgrounds = {
  aluminium: aluminiumHero,
  bronze: bronzeHero,
  copper: copperHero,
  brass: brassHero,
  "stainless-steel": stainlessSteelHero,
  "iron-and-steel": ironAndSteelHero,
  lead: leadHero,
  zinc: zincHero,
  tin: tinHero,
  nickel: nickelHero,
}

const PricePage = props => {
  const { i18n, t } = useTranslation()

  const pageContext = props.pageContext
  const country = pageContext.country
  const countryName = country.name
  const sluggedCountryName = slugMaterialName(countryName)
  const availableCountries = pageContext.availableCountries
  const materialName = pageContext.header.materialName
  const sluggedMaterialName = slugMaterialName(materialName)
  const categoriesInfo = pageContext.categoriesInfo
  if (i18n.language !== pageContext.language) {
    i18n.changeLanguage(pageContext.language)
  }

  const locale = getValidLocaleCode(
    pageContext.language,
    pageContext.country.isoCode || "DE"
  )
  const localisedUpdateDate = formatDate(
    new Date(pageContext.header.dt),
    locale
  )
  const localisedLastPriceByParts = formatCurrencyFinancialToParts(
    pageContext.header.price,
    locale,
    pageContext.country.currency[0]
  )

  const isInternational = countryName === "International"

  const countryLabel = !isInternational ? countryName : "International"
  const localisedCountryName = t(countryName)

  const localisedMaterialName = t(sluggedMaterialName + "_material_name")

  let htmlTitle = !isInternational
    ? t(`html_title_page4`) // #4
    : t(`html_title_page2`) // #2
  htmlTitle = htmlTitle
    .replace("[%1]", localisedMaterialName)
    .replace("[%2]", localisedUpdateDate)
    .replace("[%3]", localisedCountryName)

  const localisedMaterialNameForDescription = t(
    sluggedMaterialName + "_material_name"
  )

  let htmlDescription = !isInternational
    ? t(`html_description_4`) // #4
    : t(`html_description_2`) // #2
  htmlDescription = htmlDescription
    .replace("[%1]", localisedMaterialNameForDescription)
    .replace("[%2]", localisedCountryName)

  const breadcrumbItems = [
    { label: "Home", url: "/" },
    { label: "All_Scrap_Prices", url: "/scrap-metal-price/" },
    ...(countryName !== "International"
      ? [
          {
            label: countryLabel,
            url:
              pageContext.language !== "en"
                ? `/${pageContext.language}/scrap-metal-price/${sluggedCountryName}`
                : `/scrap-metal-price/${sluggedCountryName}`,
          },
        ]
      : [
          {
            label: countryLabel,
            url:
              pageContext.language !== "en"
                ? `/${pageContext.language}/scrap-metal-price/`
                : "/scrap-metal-price/",
          },
        ]),
    { label: sluggedMaterialName + "_material_name" },
  ]

  const localStoragePrefix = "selectedPriceButtonIndex"

  // Set default value to 6M for filter buttons
  const DEFAULT_RANGE_TEXT_INDEX = 2 // 6Months

  // Ensures the default range is set in localStorage, and removes any unrelated keys that share the same prefix.
  useEffect(() => {
    const rangeGroupKey = `${localStoragePrefix}_rangeGroup`
    const existingValue = localStorage.getItem(rangeGroupKey)

    if (
      existingValue === null ||
      existingValue !== DEFAULT_RANGE_TEXT_INDEX.toString()
    ) {
      localStorage.setItem(rangeGroupKey, DEFAULT_RANGE_TEXT_INDEX)
    }

    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(localStoragePrefix) && !key.endsWith("_rangeGroup")) {
        localStorage.removeItem(key)
      }
    })
  }, [])

  return (
    <Layout
      pageContext={pageContext}
      pageLocation={props.location}
      theme={"white-bg"}
      html={{
        title: htmlTitle,
        description: htmlDescription,
      }}
    >
      <BreadCrumb items={breadcrumbItems} />

      <HeaderMetal
        {...pageContext.header}
        priceByParts={localisedLastPriceByParts}
        countryName={countryName}
        language={pageContext.language}
        background={
          heroBackgrounds[sluggedMaterialName.toLowerCase()] || copperHero
        }
        date={localisedUpdateDate}
        updated={localisedUpdateDate}
        currency={country.currency}
        unit={country.unit}
        renderTitleAsH1={true}
      />
      <TextBanner
        countryName={countryName}
        dataEvent={{
          id: "Pro Form All",
          triggers: "GetQuote",
          source: "PricePages",
        }}
      />
      <ScrapMetalPriceDetail
        materialPrices={pageContext.materialPrices}
        countryCurrency={pageContext.countryCurrency}
        countryCurrencies={pageContext.countryCurrencies}
        materialName={pageContext.header.materialName}
        countryName={pageContext.country.name}
        locale={locale}
        localStoragePrefix={localStoragePrefix}
        defaultButtonIndex={DEFAULT_RANGE_TEXT_INDEX}
      />
      <PriceAlertsBanner
        language={pageContext.language}
        dataEvent={{
          id: "Price alert clicked",
        }}
      />
      <ListOfCountries
        availableCountries={availableCountries}
        allCountries={pageContext.allCountries}
        language={pageContext.language}
        sluggedMaterialName={sluggedMaterialName}
        hideDescription={true}
      />
      <MessageBanner
        theme="bg-dblue"
        dataEvent={{
          id: "Pro Form All",
          triggers: "GetQuote",
          source: "PricePages",
        }}
      />
      <ScrapMetalPriceIndex
        availableCountries={availableCountries}
        allCountries={pageContext.allCountries}
        country={countryName}
        currentMaterial={materialName}
        categoriesInfo={categoriesInfo}
        locale={locale}
        currency={pageContext.country.currency}
        language={pageContext.language}
      />

      {isInternational && (
        <CasesCarrouselSection
          dataEvent={{
            id: "Case Studies All",
            triggers: "ExploreCaseStudies",
            source: "PricePages",
          }}
        />
      )}

      <OperationalExcellence />
      <BuySellBanner
        dataEvents={[
          {
            id: "Contact Form All",
            triggers: "BuyNow",
            source: "PricePages",
          },
          null,
        ]}
      />
    </Layout>
  )
}

export default PricePage

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
